<template>
  <v-container>
    <page-heading>{{ pageTitle }}</page-heading>

    <v-tabs
      v-model="currentTab"
      class="elevation-1"
    >
      <v-tab>{{ transPlural('transaction.transactionsCreatedAndDone') }}</v-tab>
      <v-tab>
        <v-badge
          v-if="numberOfBookingsWithMissingTransactions > 0"
          color="warning"
          :content="numberOfBookingsWithMissingTransactions"
          class="mt-1"
        >
          {{ transPlural('transaction.transactionsMissing') }}
        </v-badge>
        <template v-else>{{ transPlural('transaction.transactionsMissing') }}</template>
      </v-tab>
    </v-tabs>
    <v-tabs-items
      v-model="currentTab"
    >
      <v-tab-item>
        <ajax-table
          ref="transactionsTable"
          :resource="baseResourceUrl"
          :headers="[
            {text: 'booking.bookingId', value: 'booking.id'},
            {text: 'transaction.status', value: 'status'},
            {text: 'transaction.mailStatus', value: 'mailStatus'},
            {text: 'object.object', value: 'booking.objectName'},
            {text: 'guest.guest', value: 'guestName'},
            {text: 'general.amount', value: 'refundAmount', type: 'currency'},
            {text: 'booking.checkinAt', value: 'booking.checkinAt'},
            {text: 'booking.checkoutAt', value: 'booking.checkoutAt'},
            {text: 'general.createdAt', value: 'createdAt', dateTimeFormat: 'L'},
            {text: 'transaction.downloadedAt', value: 'downloadedAt', dateTimeFormat: 'L'},
            {text: '', value: 'actions', sortable: false}
          ]"
          :page-options="{itemsPerPage: 20}"
          :items-per-page-options="[10, 20, 50]"
          :filter="transactionsFilter"
          selectable
          @items-loaded="setPageTransactionItems"
          @item-selection-changed="onTransactionsSelected"
        >
          <template v-slot:filters>
            <v-col cols="4">
              <date-period-select v-model="transactionsFilterData.checkOut" :label="trans('booking.checkoutAt')"/>
            </v-col>
            <v-col cols="4">
              <select-object :object-id.sync="transactionsFilterData.objectId" clearable hide-details/>
            </v-col>
            <v-col cols="4">
              <search v-model="transactionsFilterData.search" />
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="transactionsFilterData.status"
                :label="trans('transaction.filterByStatus')"
                :items="transactionStatusOptions"
                hide-details
                multiple
                clearable
                style="max-width: 50%;"
              >
                <template v-slot:selection="{item}">
                  <transaction-status :value="item" class="mt-2" />
                </template>

                <template v-slot:item="{item, on, attrs}">
                  <v-list-item v-bind="attrs" v-on="on">
                    <v-list-item-action>
                      <v-icon>{{ transactionsFilterData.status && transactionsFilterData.status.includes(item)
                        ? 'mdi-checkbox-marked'
                        : 'mdi-checkbox-blank-outline'
                        }}</v-icon>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>
                        <transaction-status :value="item" />
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                </template>
              </v-select>
            </v-col>
          </template>

          <template v-slot:actions>
            <v-btn
              @click="onTransactionDownloadButtonClicked"
              :disabled="selectedTransactions.length === 0"
              :loading="downloadingTransactionSepa"
            >
              <v-icon left>mdi-download</v-icon>{{ trans('transactions.downloadSelectedSepas') }}
              <template v-if="selectedTransactions.length > 0">({{ selectedTransactions.length }})</template>
            </v-btn>

            <v-btn
              class="ml-2"
              @click="onTransactionPackageDownloadButtonClicked"
              :loading="downloadingTransactionSepa"
            >
              <v-icon left>mdi-download</v-icon>{{ trans('transactions.downloadSepaPackage') }}
              <template v-if="notDownloadedTransactionsCount">
                <template v-if="notDownloadedTransactionsCount.available > notDownloadedTransactionsCount.limit">
                  ({{notDownloadedTransactionsCount.limit}}/{{notDownloadedTransactionsCount.available}})
                </template>
                <template v-else>
                  ({{notDownloadedTransactionsCount.available}})
                </template>
              </template>
            </v-btn>
          </template>

          <template v-slot:item.booking.id="{ item }">
            <div style="white-space: nowrap;">
              <booking-id :value="item.booking" :open-in-new-tab="false" style="display: inline-block;" />
              <booking-status-canceled v-if="item.booking && item.booking.canceled" display-mode="icon" small class="ml-1"></booking-status-canceled>
            </div>
          </template>

          <template v-slot:item.status="{ item }">
            <transaction-status :value="item.status"/>
          </template>

          <template v-slot:item.mailStatus="{ item }">
            <transaction-guest-mail-status :transaction="item" :status="item.guestMailStatus" />
          </template>

          <template v-slot:item.booking.objectName="{ item }">
            <object-name :value="item.booking" />
          </template>

          <template v-slot:item.guestName="{ item }">
            <guest-name :value="item.guest"/>
          </template>

          <template v-slot:item.actions="{ item }">
            <transaction-context-menu
              :transaction="item"
              @transaction-changed="onTransactionChanged"
            ></transaction-context-menu>
          </template>
        </ajax-table>
      </v-tab-item>
      <v-tab-item eager>
        <ajax-table
          ref="bookingsMissingTransactionsTable"
          :resource="baseResourceUrl + '/missing'"
          :headers="[
              {text: 'booking.bookingId', value: 'bookingId'},
              {text: 'booking.validationErrorCodes', value: 'transactionErrorCodes'},
              {text: 'object.object', value: 'objectName'},
              {text: 'guest.guest', value: 'guestName'},
              {text: 'booking.checkinAt', value: 'checkinAt'},
              {text: 'booking.checkoutAt', value: 'checkoutAt'},
              {text: 'booking.deposit', value: 'amountDeposit', type: 'currency'},
              {text: 'booking.amountTransaction', value: 'calculatedTransactionResultAmount', type: 'currency', sortable: false},
              {text: '', value: 'actions', sortable: false},
            ]"
          :page-options="{itemsPerPage: 20}"
          :items-per-page-options="[10, 20, 50]"
          :filter="bookingsMissingTransactionsFilter"
          @page-loaded="onBookingsLoaded"
          selectable
          @item-selection-changed="onBookingTransactionsSelected"
        >
          <template v-slot:filters>
            <v-col cols="4">
              <date-period-select v-model="bookingsMissingTransactionsFilterData.checkOut" :label="trans('booking.checkoutAt')"/>
            </v-col>
            <v-col cols="4">
              <select-object :object-id.sync="bookingsMissingTransactionsFilterData.objectId" clearable hide-details/>
            </v-col>
            <v-col cols="4">
              <search v-model="bookingsMissingTransactionsFilterData.search" />
            </v-col>
          </template>

          <template v-slot:actions>
            <v-btn
              @click="onBookingTransactionCreateButtonClicked"
              :disabled="selectedBookingTransactions.length === 0"
              :loading="creatingTransactionsFromBookings"
              class="mr-2"
            >
              <v-icon left>mdi-plus-circle</v-icon>{{ trans('transactions.createFromBooking') }}
              <template v-if="selectedBookingTransactions.length > 0">({{ selectedBookingTransactions.length }})</template>
            </v-btn>
            <v-btn
              @click="markBookingsWithNoRefundAsNotNeeded"
              :loading="markingBookingsRefundAsNotNeeded"
            >
              {{ trans('transaction.markBookingsRefundAsNotNeeded') }}
              <help-tooltip translation-key="transaction.markBookingsRefundAsNotNeededHelp"/>
            </v-btn>
            <!--        TODO add objects filter and more if fitting-->
          </template>

          <template v-slot:item.bookingId="{ item }">
            <booking-id :value="item" :open-in-new-tab="false" />
          </template>

          <template v-slot:item.transactionErrorCodes="{ item }">
            <template v-if="item.transactionErrorCodes != null">
              <booking-validation-error
                v-for="errorCode in item.transactionErrorCodes"
                :key="errorCode"
                :value="errorCode"
                :only-icon="true"
                :booking="item"
              />
            </template>
          </template>

          <template v-slot:item.objectName="{ item }">
            <object-name :value="item" />
          </template>

          <template v-slot:item.guestName="{ item }">
            <guest-name :value="item"/>
          </template>

          <template v-slot:item.calculatedTransactionResultAmount="{ item }">
            <currency-amount
              v-if="item.calculatedTransaction"
              :amount="item.calculatedTransaction.resultAmount"
            />
            <template v-else>&ndash;</template>
          </template>
        </ajax-table>
      </v-tab-item>
    </v-tabs-items>

    <validation-errors-modal
      :title="trans('general.couldNotDownloadSepa')"
      :show-modal.sync="showSepaValidationErrorsModal"
      :validation-errors.sync="sepaValidationErrors"
    >
    </validation-errors-modal>

    <confirm-modal
      :title="trans('transactions.downloadSelectedSepas')"
      :show-modal.sync="showDownloadTransactionSepaConfirmModal"
      :confirm-button-label="trans('general.download')"
      @confirm="downloadTransactionSepaAllSelected"
      max-width="550"
    >
      {{ trans('transactions.downloadSelectedSepasIncludingAlreadyDownloadedHelp') }}
    </confirm-modal>

    <confirm-modal
      :title="trans('transactions.createFromBooking')"
      :show-modal.sync="showCreateTransactionsConfirmModal"
      :confirm-button-label="trans('transactions.createFromBooking')"
      @confirm="createRefundTransactionFromBookings"
    >
      {{ trans('transactions.createFromBookingConfirm') }}
    </confirm-modal>

    <validation-errors-modal
      :show-modal.sync="showCreateTransactionFromBookingResultsModal"
      :title="trans('transactions.createFromBooking')"
      :validation-errors="createTransactionFromBookingResults"
    />
  </v-container>
</template>

<script>
import AjaxTable from '../components/AjaxTable';
import {LocaleUtils} from '../utils/locale-utils';
import PageHeading from '../components/PageHeading';
import {DateUtils} from '../utils/date-utils';
import {RouterUtils} from '../utils/router-utils';
import {MainUtils} from '../utils/main-utils';
import {TransactionApi} from '../api/transaction-api';
import ConfirmModal from '../components/ConfirmModal';
import ValidationErrorsModal from '../components/ValidationErrorsModal';
import {SepaValidationErrorsUtils} from '../utils/sepa-validation-errors-utils';
import {comparison, eq, inList, ne, or} from 'rsql-builder';
import SelectObject from '@/components/SelectObject';
import Search from '@/components/Search';
import ObjectName from '@/components/ObjectName';
import DatePeriodSelect from '@/components/DatePeriodSelect';
import {FilterUtils} from '@/utils/filter-utils';
import TransactionStatus from '@/components/TransactionStatus';
import BookingId from "@/components/BookingId";
import GuestName from "@/components/GuestName";
import TransactionGuestMailStatus from '../components/TransactionGuestMailStatus';
import {TransactionStatus as TransactionStatusEnum} from '@/lib/transaction-status';
import BookingStatusCanceled from '@/components/BookingStatusCanceled';
import TransactionContextMenu from '@/components/TransactionContextMenu';
import BookingValidationError from '../components/BookingValidationError';
import HelpTooltip from '@/components/icons/HelpTooltip';
import CurrencyAmount from '../components/CurrencyAmount.vue';

export default {
  name: 'TransactionsPage',
  mixins: [
    LocaleUtils,
    DateUtils,
    RouterUtils,
    MainUtils,
    SepaValidationErrorsUtils,
    FilterUtils
  ],
  components: {
    CurrencyAmount,
    HelpTooltip,
    BookingValidationError,
    TransactionContextMenu,
    BookingStatusCanceled,
    TransactionGuestMailStatus,
    TransactionStatus,
    ObjectName,
    DatePeriodSelect,
    Search,
    SelectObject,
    ValidationErrorsModal,
    ConfirmModal,
    PageHeading,
    AjaxTable,
    BookingId,
    GuestName
  },
  data: () => ({
    transactionsFilterData: {
      status: [
        TransactionStatusEnum.WAITING_FOR_DOWNLOAD,
        TransactionStatusEnum.WAITING_FOR_EMAIL,
        TransactionStatusEnum.WAITING_FOR_PAYBACK
      ],
      objectId: null,
      search: null,
      checkOut: null
    },
    /** @type {TransactionDto[]} */
    pageTransactionItems: [],
    /** @type {TransactionDto[]} */
    selectedTransactions: [],
    /** @type {NotDownloadedTransactionsCount} */
    notDownloadedTransactionsCount: null,
    showDownloadTransactionSepaConfirmModal: false,
    downloadingTransactionSepa: false,
    numberOfBookingsWithMissingTransactions: null,
    currentTab: null,
    /** @type {BookingTransactionDto[]} */
    selectedBookingTransactions: [],
    creatingTransactionsFromBookings: false,
    showCreateTransactionsConfirmModal: false,
    /** @type {CreateTransactionFromBookingResults} */
    createTransactionFromBookingResults: null,
    showCreateTransactionFromBookingResultsModal: false,
    markingBookingsRefundAsNotNeeded: false,
    TransactionStatusEnum: TransactionStatusEnum,
    bookingsMissingTransactionsFilterData: {
      objectId: null,
      search: null,
      checkOut: null
    }
  }),

  computed: {
    transactionsFilter () {
      let filters = [];
      if(this.transactionsFilterData.status != null && this.transactionsFilterData.status.length > 0) {
        filters.push(comparison('status', inList(...this.transactionsFilterData.status)))
      }
      if(this.transactionsFilterData.objectId != null) {
        filters.push(comparison('object.id', eq(this.transactionsFilterData.objectId)))
      }
      if(this.transactionsFilterData.search) {
        filters.push(this.buildSearchFilter(this.transactionsFilterData.search, ['object.name','guest.firstName','guest.lastName','booking.id']));
      }
      if(this.transactionsFilterData.checkOut != null) {
        filters.push(this.buildDateFilter('booking.checkoutAt', this.transactionsFilterData.checkOut));
      }
      return filters;
    },
    bookingsMissingTransactionsFilter () {
      let filters = [];
      if(this.bookingsMissingTransactionsFilterData.objectId != null) {
        filters.push(comparison('object.id', eq(this.bookingsMissingTransactionsFilterData.objectId)))
      }
      if(this.bookingsMissingTransactionsFilterData.search) {
        filters.push(this.buildSearchFilter(this.bookingsMissingTransactionsFilterData.search, ['object.name','guest.firstName','guest.lastName','id']));
      }
      if(this.bookingsMissingTransactionsFilterData.checkOut != null) {
        filters.push(this.buildDateFilter('checkoutAt', this.bookingsMissingTransactionsFilterData.checkOut));
      }
      return filters;
    },
    baseResourceUrl () {
      return TransactionApi.apiUri;
    },
    pageTitle () {
      return this.transPlural('transaction.transaction');
    },
    transactionStatusOptions () {
      return Object.values(TransactionStatusEnum);
    }
  },

  mounted () {
    this.getNotDownloadedTransactionsCount();
  },

  activated () {
    this.$nextTick(() => {
      if (this.$refs.transactionsTable) {
        this.$refs.transactionsTable.loadData();
      }
      if (this.$refs.bookingsMissingTransactionsTable) {
        this.$refs.bookingsMissingTransactionsTable.loadData();
      }
      this.getNotDownloadedTransactionsCount();
    });
  },

  methods: {
    /** @type {TransactionDto[]} */
    onTransactionsSelected (selectedTransactions) {
      this.selectedTransactions = selectedTransactions || [];
    },
    /** @type {TransactionDto[]} */
    setPageTransactionItems (pageTransactionItems) {
      this.pageTransactionItems = pageTransactionItems;
    },
    onTransactionDownloadButtonClicked () {
      if (this.selectedTransactions && this.selectedTransactions.length > 0) {
        let alreadyDownloadedTransactionsSelected = this.selectedTransactions.filter(transaction => transaction.downloadedAt !== null).length > 0;

        if (alreadyDownloadedTransactionsSelected) {
          this.showDownloadTransactionSepaConfirmModal = true;
        } else {
          this.downloadTransactionSepaAllSelected();
        }
      }
    },
    onTransactionPackageDownloadButtonClicked () {
      this.downloadingTransactionSepa = true;
      TransactionApi.getNotDownloadedTransactionsSepa()
      .then(this.downloadResponseAsFile)
      .then(this.onTransactionChanged)
      .catch(this.handleSepaValidationErrorResponse)
      .finally(() => {
        this.downloadingTransactionSepa = false;
      })
    },
    downloadTransactionSepa (transactionIds) {
      this.downloadingTransactionSepa = true;
      TransactionApi.getTransactionsSepa(transactionIds)
      .then(this.downloadResponseAsFile)
      .then(this.onTransactionChanged)
      .catch(this.handleSepaValidationErrorResponse)
      .finally(() => {
        this.downloadingTransactionSepa = false;
      })
    },
    downloadTransactionSepaAllSelected () {
      if (this.selectedTransactions && this.selectedTransactions.length > 0) {
        this.downloadTransactionSepa(
          this.selectedTransactions.map(transaction => transaction.id)
        );
      }
    },
    onBookingsLoaded (response) {
      if (Object.keys(this.bookingsMissingTransactionsFilter).length === 0) {
        this.numberOfBookingsWithMissingTransactions = response.totalElements;
      }
    },
    /** @type {BookingTransactionDto[]} */
    onBookingTransactionsSelected (selectedBookingTransactions) {
      this.selectedBookingTransactions = selectedBookingTransactions || [];
    },
    onBookingTransactionCreateButtonClicked () {
      this.showCreateTransactionsConfirmModal = true;
    },
    createRefundTransactionFromBookings () {
      this.creatingTransactionsFromBookings = true;
      TransactionApi.createRefundTransactionFromBookings(this.selectedBookingTransactions.map(bookingTransaction => bookingTransaction.id))
      .then((response) => {
        const validationErrors = response.data;
        if (validationErrors && Object.getOwnPropertyNames(validationErrors).length > 0) {
          this.showCreateTransactionFromBookingResultsModal = true;
        }
        this.createTransactionFromBookingResults = validationErrors;
        this.$refs.transactionsTable.loadData();
        this.$refs.bookingsMissingTransactionsTable.loadData();
      })
      .catch(this.handleErrorResponse)
      .finally(() => {
        this.creatingTransactionsFromBookings = false;
        this.showCreateTransactionsConfirmModal = false;
      })
    },
    markBookingsWithNoRefundAsNotNeeded() {
      this.markingBookingsRefundAsNotNeeded = true;
      TransactionApi.markBookingsWithNoRefundAsNotNeeded()
      .then((response) => {
        this.$refs.bookingsMissingTransactionsTable.loadData();
      })
      .catch(this.handleErrorResponse)
      .finally(() => {
        this.markingBookingsRefundAsNotNeeded = false;
      })
    },
    onTransactionChanged () {
      this.$refs.transactionsTable.loadData();
      this.getNotDownloadedTransactionsCount();
    },
    getNotDownloadedTransactionsCount() {
      TransactionApi.getNotDownloadedTransactionsCount()
      .then((response) => {
        this.notDownloadedTransactionsCount = response.data;
      })
      .catch(this.handleErrorResponseSilent)
    }
  },

  watch: {
    currentTab (currentTabNew, currentTabOld) {
      if (currentTabOld !== null) {
        setTimeout(() => {
          switch (this.currentTab) {
            case 0: if (this.$refs.transactionsTable) this.$refs.transactionsTable.loadData(); break;
            case 1: if (this.$refs.bookingsMissingTransactionsTable) this.$refs.bookingsMissingTransactionsTable.loadData(); break;
          }
        }, 500);
      }
    }
  }
}
</script>

<style>

</style>
